import React, { useState, useEffect, useCallback } from 'react';
import { Joyride, STATUS, ACTIONS } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../utils/api';

const STORAGE_KEY_PREFIX = 'onboarding_completed_';

// iter 316 — 9 lépéses, az új (iter 311) menühöz igazított túra; hiányzó cél esetén a Joyride átugorja a lépést
const buildSteps = (lang) => {
  const hu = lang === 'hu';
  const steps = hu ? [
    { title: 'Üdv az Acountix-ban! 👋', body: 'Pár lépésben megmutatjuk, hol találod a legfontosabb funkciókat. Bármikor kihagyhatod — a Beállítások › Útmutató oldalról később újraindítható.' },
    { target: '[data-testid="nav-group-toggle-clients"]', title: 'Ügyfelek & Munkák', body: 'Ügyfelek, projektek, árajánlatok, árlista és termékek egy helyen. A Megrendelések pontban futnak be a megrendelői portálon leadott megrendelések — egy kattintással projekt lesz belőlük.' },
    { target: '[data-testid="nav-group-toggle-sales"]', title: 'Számlázás', body: 'Kimenő és bejövő számlák, ismétlődő számlák, QR-fizetés, számlázó-integrációk (Billingo, Faktury). Az elfogadott ajánlatból egy kattintással számla lesz.' },
    { target: '[data-testid="nav-group-toggle-finance"]', title: 'Pénzügy', body: 'Banki egyeztetés, tartozások automatikus emlékeztetőkkel, fizetések — minden egy helyen.' },
    { target: '[data-testid="nav-group-toggle-team"]', title: 'Csapat', body: 'Dolgozók és alvállalkozók, jelenléti ív, Vezetői naptár és műszak-beosztás. A dolgozóid saját Acountix dolgozói fiókkal (AX-azonosító vagy QR) lépnek be a telefonjukon — be-/kijelentkezés GPS-szel, feladatok, szabadság.' },
    { target: '[data-testid="nav-group-toggle-insights"]', title: 'Elemzés & Automatizálás', body: 'Riportok, előrejelzés, AI-asszisztens, automatizálások és marketing. A Dokumentumok csoportban a projekt-dokumentumok és az archívum.' },
    { target: '[data-testid="dashv2-menu-trigger"]', placement: 'bottom', title: 'Az irányítópult a tiéd', body: 'Itt kapcsolhatod ki/be a csempéket és blokkokat, húzással átrendezheted őket, és válthatsz letisztult / színes stílus között.' },
    { target: '[data-testid="copilot-fab"]', placement: 'left', title: 'Acountix Copilot', body: 'A jobb alsó gombbal bármelyik oldalon segítséget kérhetsz: „Rakd be a burkolás tételeit az ajánlatba”, „Ki jár le a héten?” — a Copilot kitölti, te csak jóváhagyod.' },
    { target: '[data-testid="nav-settings"]', title: 'Beállítások és integrációk', body: 'Cégadatok, logó, aláírás, e-mail (SMTP), számlázó-integrációk, API-kulcsok, Lomtár és Útmutató. Sok sikert! 🚀' },
  ] : [
    { title: 'Vitajte v Acountixe! 👋', body: 'V pár krokoch vám ukážeme, kde nájdete najdôležitejšie funkcie. Kedykoľvek môžete preskočiť — znova spustíte v Nastavenia › Príručka.' },
    { target: '[data-testid="nav-group-toggle-clients"]', title: 'Klienti & Zákazky', body: 'Klienti, projekty, ponuky, cenník a produkty. V bode Objednávky prichádzajú objednávky z klientskeho portálu — jedným klikom z nich bude projekt.' },
    { target: '[data-testid="nav-group-toggle-sales"]', title: 'Fakturácia', body: 'Vystavené a prijaté faktúry, opakované faktúry, QR platby, integrácie (Billingo, Faktury).' },
    { target: '[data-testid="nav-group-toggle-finance"]', title: 'Financie', body: 'Bankové párovanie, pohľadávky s automatickými pripomienkami, platby.' },
    { target: '[data-testid="nav-group-toggle-team"]', title: 'Tím', body: 'Zamestnanci a subdodávatelia, dochádzka, manažérsky kalendár a plán zmien. Zamestnanci sa prihlasujú vlastným účtom (AX-kód alebo QR) na telefóne — dochádzka s GPS, úlohy, dovolenka.' },
    { target: '[data-testid="nav-group-toggle-insights"]', title: 'Analýza & Automatizácia', body: 'Reporty, predikcie, AI asistent, automatizácie a marketing. V skupine Dokumenty: projektové dokumenty a archív.' },
    { target: '[data-testid="dashv2-menu-trigger"]', placement: 'bottom', title: 'Dashboard je váš', body: 'Tu zapnete/vypnete dlaždice a bloky, presuniete ich ťahaním a prepnete čistý / farebný štýl.' },
    { target: '[data-testid="copilot-fab"]', placement: 'left', title: 'Acountix Copilot', body: 'Tlačidlo vpravo dole: požiadaj o pomoc na ktorejkoľvek stránke — Copilot vyplní, ty schváliš.' },
    { target: '[data-testid="nav-settings"]', title: 'Nastavenia a integrácie', body: 'Firemné údaje, logo, podpis, SMTP, fakturačné integrácie, Kôš a Príručka. Veľa šťastia! 🚀' },
  ];

  return steps.map((s) => ({
    target: s.target || 'body',
    placement: s.target ? (s.placement || 'right') : 'center',
    disableBeacon: true,
    title: s.title,
    content: s.body,
  }));
};

export default function OnboardingTour() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const [tourKey, setTourKey] = useState(0);

  const storageKey = user?.id ? `${STORAGE_KEY_PREFIX}${user.id}` : null;

  const markDone = useCallback(() => {
    if (storageKey) localStorage.setItem(storageKey, '1');
    try {
      localStorage.setItem('onboardingCompleted', 'true');
      localStorage.setItem('hasSeenOnboarding', 'true');
    } catch (_) { /* ignore */ }
    // Notify other widgets (eg. OnboardingChecklist) to hide immediately
    try { window.dispatchEvent(new Event('onboarding:completed')); } catch (_) { /* ignore */ }
    // Also persist to backend so clearing cache / using another device doesn't resurface the tour
    try {
      axios.post(`${API_URL}/api/users/me/onboarding-seen`, {}, { withCredentials: true }).catch(() => {});
    } catch (_) { /* ignore */ }
  }, [storageKey]);

  // Auto-start for first-time users (dashboard only)
  useEffect(() => {
    if (!user || !storageKey) return;
    // iter 190 — on mobile (<768px) the MobileOnboardingTour takes over.
    if (typeof window !== 'undefined' && window.innerWidth < 768) return;
    if (localStorage.getItem(storageKey)) return;
    // iter 187 — generic dismissal keys also suppress the tour
    // (used by automated tests + Settings → "Onboarding már elvégezve" toggle)
    if (
      localStorage.getItem('onboardingCompleted') === 'true' ||
      localStorage.getItem('hasSeenOnboarding') === 'true' ||
      localStorage.getItem('onboarding_dismissed') === '1'
    ) {
      // Sync user-specific key to keep state consistent
      localStorage.setItem(storageKey, '1');
      return;
    }
    // If the server already recorded that this user saw the tour, sync it and bail
    if (user.onboarding_seen) {
      localStorage.setItem(storageKey, '1');
      return;
    }
    if (location.pathname !== '/') return;
    setSteps(buildSteps(i18n.language));
    const tmr = setTimeout(() => {
      setRun(true);
      // Mark as seen the moment we show the tour — guarantees it never re-appears
      // even if the user closes the tab, reloads, or navigates away mid-tour.
      markDone();
    }, 800);
    return () => clearTimeout(tmr);
  }, [user, storageKey, i18n.language, location.pathname, markDone]);

  // Manual restart via global event
  useEffect(() => {
    // iter 279 — navigáláskor a túra leáll, nem fogja el a kattintásokat más oldalakon
    if (run && location.pathname !== '/') setRun(false);
  }, [location.pathname, run]);

  useEffect(() => {
    if (location.pathname === '/' && new URLSearchParams(location.search).get('tour') === '1') {
      window.history.replaceState({}, '', '/');
      setTimeout(() => window.dispatchEvent(new Event('onboarding-restart')), 600);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handler = () => {
      if (storageKey) localStorage.removeItem(storageKey);
      // Reset the backend flag so a manual replay works (from Settings / help menu)
      axios.post(`${API_URL}/api/users/me/onboarding-reset`, {}, { withCredentials: true }).catch(() => {});
      setSteps(buildSteps(i18n.language));
      setRun(false);
      setTourKey((k) => k + 1);
      setTimeout(() => setRun(true), 300);
    };
    window.addEventListener('onboarding-restart', handler);
    return () => window.removeEventListener('onboarding-restart', handler);
  }, [i18n.language, storageKey]);

  const handleCallback = useCallback((data) => {
    const { status, action } = data;

    if (action === ACTIONS.CLOSE) {
      setRun(false);
      markDone();
      return;
    }

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      markDone();
    }
  }, [markDone]);

  if (!user || !steps.length) return null;

  const hu = i18n.language === 'hu';

  return (
    <Joyride
      key={tourKey}
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      disableOverlayClose
      disableScrolling
      spotlightPadding={6}
      callback={handleCallback}
      locale={{
        back: hu ? 'Vissza' : 'Späť',
        close: hu ? 'Bezár' : 'Zavrieť',
        last: hu ? 'Kész' : 'Hotovo',
        next: hu ? 'Tovább' : 'Ďalej',
        skip: hu ? 'Kihagyás' : 'Preskočiť',
      }}
      styles={{
        options: {
          primaryColor: '#D97036',
          zIndex: 10000,
          arrowColor: '#ffffff',
          backgroundColor: '#ffffff',
          textColor: '#0F1629',
          overlayColor: 'rgba(14, 18, 32, 0.5)',
          width: 480,
        },
        tooltip: {
          borderRadius: 16,
          padding: 24,
          fontSize: 14,
          boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
        },
        tooltipTitle: {
          fontSize: 20,
          fontWeight: 700,
          marginBottom: 10,
        },
        tooltipContent: {
          fontSize: 14,
          lineHeight: 1.6,
          padding: '8px 0',
        },
        buttonNext: {
          borderRadius: 10,
          padding: '10px 20px',
          fontSize: 14,
          fontWeight: 600,
          backgroundColor: '#0A0A0A',
        },
        buttonBack: {
          color: '#475569',
          fontSize: 13,
          marginRight: 8,
        },
        buttonSkip: {
          color: '#94A3B8',
          fontSize: 13,
        },
      }}
    />
  );
}
